import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiChevronDown, FiChevronUp, FiHelpCircle } = FiIcons;

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqCategories = [
    {
      title: 'General Questions',
      faqs: [
        {
          question: 'What is NinjaCopia and how does it work?',
          answer: 'NinjaCopia is a professional trade copying add-on for NinjaTrader 8 that automatically replicates trades from your Master account to multiple follower accounts with <5ms latency. You simply connect your accounts, configure risk settings, and trade normally on your Master account - NinjaCopia handles the rest.'
        },
        {
          question: 'Do I need a separate NinjaTrader license for each account?',
          answer: 'Yes, each NinjaTrader account requires its own valid NinjaTrader license. NinjaCopia works with your existing NinjaTrader setup and does not replace the need for proper NinjaTrader licensing.'
        },
        {
          question: 'Is NinjaCopia officially approved by NinjaTrader?',
          answer: 'We are currently in the process of applying for official NinjaTrader vendor licensing. Join our waitlist to receive updates on our approval status and early access when available.'
        },
        {
          question: 'What brokers and data feeds are supported?',
          answer: 'NinjaCopia supports all major NinjaTrader-compatible brokers including Rithmic, Continuum, ATAS, and Interactive Brokers. If you can connect it to NinjaTrader 8, NinjaCopia can copy trades to it.'
        }
      ]
    },
    {
      title: 'Technical Requirements',
      faqs: [
        {
          question: 'What are the minimum system requirements?',
          answer: 'Minimum requirements: Windows 10 64-bit, Intel i5 4th gen or AMD equivalent, 8 GB RAM, 500 MB disk space, NinjaTrader 8.0.29.1+, and stable 10+ Mbps internet. See our Features page for recommended specifications.'
        },
        {
          question: 'Can I run NinjaCopia on multiple computers?',
          answer: 'Yes, our multi-machine licenses allow you to run NinjaCopia on 2 or 3 computers simultaneously. Each license is tied to specific machine IDs for security and can be transferred to new hardware through our self-service portal.'
        },
        {
          question: 'Does NinjaCopia work with simulated accounts?',
          answer: 'Yes, NinjaCopia works with both live and simulated accounts. We recommend testing your setup thoroughly with sim accounts before going live with real money.'
        },
        {
          question: 'What happens if my internet connection is unstable?',
          answer: 'NinjaCopia includes connection monitoring and automatic reconnection features. However, a stable internet connection is essential for reliable trade copying. We recommend a dedicated trading connection with at least 10 Mbps speed.'
        }
      ]
    },
    {
      title: 'Pricing & Licensing',
      faqs: [
        {
          question: 'What happens after the 1-year free update period?',
          answer: 'You keep your licensed version forever and can continue using it indefinitely. Major version updates (like NinjaCopia 2.0) may require an upgrade fee, but minor updates and bug fixes within your version remain free.'
        },
        {
          question: 'Can I transfer my license to a new computer?',
          answer: 'Yes, you can deactivate your license on one machine and reactivate it on another through our self-service portal. This process can be done instantly without contacting support.'
        },
        {
          question: 'Is there a monthly subscription fee?',
          answer: 'No, NinjaCopia is a one-time purchase with no recurring fees. You pay once and own the license forever for the purchased version.'
        },
        {
          question: 'Do you offer refunds?',
          answer: 'Yes, we offer a full 30-day money-back guarantee. If NinjaCopia doesn\'t meet your expectations, contact us within 30 days of purchase for a complete refund.'
        }
      ]
    },
    {
      title: 'Setup & Configuration',
      faqs: [
        {
          question: 'How difficult is it to set up NinjaCopia?',
          answer: 'Setup is straightforward and typically takes 10-15 minutes. Our step-by-step wizard guides you through connecting accounts, configuring risk settings, and testing the connection. Detailed documentation is available in English, Spanish, and French.'
        },
        {
          question: 'Can I copy trades to accounts with different lot sizes?',
          answer: 'Absolutely. NinjaCopia includes intelligent position sizing that can scale trades based on account equity, fixed ratios, or custom rules. Each follower account can have its own sizing configuration.'
        },
        {
          question: 'What risk management features are included?',
          answer: 'NinjaCopia includes per-account position limits, maximum drawdown protection, equity-based sizing, and real-time P&L monitoring. You can set different risk parameters for each follower account.'
        },
        {
          question: 'Can I exclude certain symbols from copying?',
          answer: 'Yes, you can create symbol filters to include or exclude specific instruments from copying. This is useful if different accounts have access to different markets or instruments.'
        }
      ]
    }
  ];

  const toggleFAQ = (categoryIndex, faqIndex) => {
    const index = `${categoryIndex}-${faqIndex}`;
    setOpenIndex(openIndex === index ? null : index);
  };

  /* Structured Data for FAQPage */
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqCategories.flatMap(category =>
      category.faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    )
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <SEO
        title="Frequently Asked Questions"
        description="Find answers to common questions about NinjaCopia trade copier, licensing, latency, and supported brokers."
        url="https://ninjacopia.com/#/faq"
        schema={faqSchema}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-900 dark:to-blue-900/20 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6"
            >
              Frequently Asked Questions
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto"
            >
              Find answers to common questions about NinjaCopia. Can't find what you're looking for? Contact our support team.
            </motion.p>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {faqCategories.map((category, categoryIndex) => (
            <div key={category.title} className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 flex items-center">
                <SafeIcon icon={FiHelpCircle} className="w-6 h-6 text-blue-600 dark:text-blue-400 mr-3" />
                {category.title}
              </h2>

              <div className="space-y-4">
                {category.faqs.map((faq, faqIndex) => {
                  const isOpen = openIndex === `${categoryIndex}-${faqIndex}`;

                  return (
                    <motion.div
                      key={faqIndex}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: (categoryIndex * category.faqs.length + faqIndex) * 0.05 }}
                      className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
                    >
                      <button
                        onClick={() => toggleFAQ(categoryIndex, faqIndex)}
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                      >
                        <h3 className="font-semibold text-gray-900 dark:text-white pr-4">
                          {faq.question}
                        </h3>
                        <SafeIcon
                          icon={isOpen ? FiChevronUp : FiChevronDown}
                          className="w-5 h-5 text-gray-500 dark:text-gray-400 shrink-0"
                        />
                      </button>

                      {isOpen && (
                        <motion.div
                          initial={{ height: 0 }}
                          animate={{ height: 'auto' }}
                          exit={{ height: 0 }}
                          transition={{ duration: 0.2 }}
                          className="border-t border-gray-200 dark:border-gray-700"
                        >
                          <div className="px-6 py-4">
                            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                              {faq.answer}
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Still Have Questions?
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-8">
            Our support team is here to help. Contact us for personalized assistance.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            Contact Support
          </a>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;